
import React, { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useJobEstimate } from '@/hooks/useJobEstimate'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface CostBadgeProps {
  provider: string | null;
  model: string | null;
  balance?: string;
  className?: string;
}

/**
 * CostBadge — displays the estimated cost for a job beside the Run button.
 *
 * Shows a loading indicator while fetching, then renders the price.
 * When the user's balance is insufficient the price turns red with a tooltip.
 */
export const CostBadge: React.FC<CostBadgeProps> = React.memo(function CostBadge({
  provider,
  model,
  balance,
  className,
}) {
  const { estimate, isLoading } = useJobEstimate(provider, model);

  const hasEnoughBalance = useMemo(() => {
    if (!balance || !estimate?.priceUsd) return true;
    const balanceNum = parseFloat(balance);
    const priceNum = parseFloat(estimate.priceUsd);
    if (isNaN(balanceNum) || isNaN(priceNum)) return true;
    return balanceNum >= priceNum;
  }, [balance, estimate]);

  if (!provider || !model) return null;
  if (isLoading) return <span className={cn('text-[10px] text-muted-foreground', className)}>...</span>;
  if (!estimate) return null;

  const badge = (
    <span
      className={cn(
        'text-[10px] font-mono leading-none cursor-default select-none',
        hasEnoughBalance ? 'text-muted-foreground' : 'text-destructive font-semibold',
        className,
      )}
      aria-label={`Estimated cost: $${estimate.priceUsd}`}
      role="status"
    >
      ${estimate.priceUsd}
    </span>
  );

  if (!hasEnoughBalance) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent size="sm" className="max-w-[180px] text-center">
          Insufficient balance (${balance}). Top up to generate.
        </TooltipContent>
      </Tooltip>
    );
  }

  return badge;
});
